/**
 * Is the viewport below the `lg` breakpoint (1024px)?
 *
 * NOT `useIsMobile`. That hook answers a different question at a different
 * boundary — 767px, the LOCKED M1/M2 layout-shell breakpoint — and widening it
 * would move the app shell. This one exists because the sync-run detail switches
 * between an inline column and an overlay at `lg`, and the CSS that hides the
 * column (`hidden lg:block`) has to agree with the JS that opens the drawer. Two
 * numbers for one boundary is how a tablet ends up seeing neither.
 *
 * The query is written as `max-width` for the same reason `useIsMobile` is: jsdom
 * ships no `matchMedia`, so a test viewport reads as NOT compact and exercises
 * the desktop path — which is the path this page is designed around. A test that
 * wants the overlay mocks `window.matchMedia` explicitly.
 */
import { useEffect, useState } from "react";

const COMPACT_QUERY = "(max-width: 1023px)";

function readMatch() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(COMPACT_QUERY).matches;
}

export function useIsCompactViewport() {
  const [compact, setCompact] = useState(readMatch);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia(COMPACT_QUERY);
    const onChange = (e) => setCompact(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return compact;
}
